import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { zone as zoneMock } from "@planzone/dto";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { resetState } from "../../utils/resetState";
import { reduceZone } from "../../common/zone";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchRequest } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    filter,
    sort,
    size,
    index,
    view,
  }: Boundaries["retrieveZonesIndicators"]["request"],
): State {
  const stateIndex = matchRequest(state, filter, view, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          request: setRequestStateToPending(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index,
          canLoadMore: true,
          zones: [],
          request: setRequestStateToPending(),
          view,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    filter,
    sort,
    size,
    index,
    zones,
    view,
  }: Boundaries["retrieveZonesIndicators"]["success"],
): State {
  const stateIndex = matchRequest(state, filter, view, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          canLoadMore: zones.length >= size,
          index: index + 1,
          zones:
            index === 0
              ? zones
              : [
                  ...state[stateIndex].zones.slice(0, index * size),
                  ...zones,
                  ...state[stateIndex].zones.slice(index * size + size),
                ],
          request: setRequestStateToSuccess(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index: index + 1,
          canLoadMore: zones.length >= size,
          zones,
          view,
          request: setRequestStateToSuccess(),
        },
      ];
}

function handleFailure(
  state: State,
  {
    filter,
    sort,
    size,
    index,
    view,
  }: Boundaries["retrieveZonesIndicators"]["failure"],
): State {
  const stateIndex = matchRequest(state, filter, view, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          request: setRequestStateToFailure(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index,
          view,
          canLoadMore: true,
          zones: [],
          request: setRequestStateToFailure(),
        },
      ];
}

function handleRemoveZone(
  state: State,
  zone: Boundaries["removeZone"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    zones: req.zones.filter(({ uuid }) => uuid !== zone.uuid),
  }));
}

function handleCreateZone(): State {
  return [];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    zones: req.zones.map((zone) => ({
      ...zone,
      ...reduceZone({ ...zoneMock, ...zone }, event),
    })),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveZonesIndicatorsRequested]: handleRequest,
      [events.retrieveZonesIndicatorsSucceeded]: handleSuccess,
      [events.retrieveZonesIndicatorsFailed]: handleFailure,
      [events.removeZoneSucceeded]: handleRemoveZone,
      [events.createZoneSucceeded]: handleCreateZone,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
